import { useState } from 'react';
import Button from '../../../../reusable/Button/Button';
import DateSelector from '../../../../reusable/DateSelector/DateSelector';
import Skeleton from '../../../../reusable/Skeleton/Skeleton';
import { toast } from '../../../../reusable/Toast/toastStore';
import { useGetAffiliateMarketingDashboardQuery } from '../../../../store/api/affiliateApi';
import { useRequestPayoutMutation } from '../../../../store/api/payoutsApi';
import { usd, NA } from '../../../../utilities/format';
import { formatDate } from '../format';
import SuperAffiliate from './SuperAffiliate';
import { apiMessage } from './promoCodes';
import { t } from '../../../../i18n/i18n';
import './Overview.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-10-16" → "16/Oct/2026", the form `/affiliate/v2/dashboard/` reads its range in. */
const rangeParam = (iso) => {
  const [y, m, d] = String(iso).split('-');
  return `${d}/${MONTHS[Number(m) - 1]}/${y}`;
};

/**
 * Affiliate Marketing — Overview. Live's overview (`NewDashboard.jsx` on main)
 * up to and including the Super Affiliate promo codes: the withdrawal terms,
 * the bonus claim and the Super Affiliate card, all from
 * `/affiliate/v2/dashboard/`.
 */
export default function Overview() {
  const [range, setRange] = useState({ startDate: null, endDate: null });
  const hasRange = Boolean(range.startDate && range.endDate);

  const { data, isLoading, isError } = useGetAffiliateMarketingDashboardQuery(
    hasRange ? { start_date: rangeParam(range.startDate), end_date: rangeParam(range.endDate) } : {}
  );
  const [requestPayout, { isLoading: claiming }] = useRequestPayoutMutation();

  const claimBonus = async () => {
    if (claiming) return;
    try {
      await requestPayout({ amount: data?.bonus_amount, type: 'Bonus' }).unwrap();
      toast.success(t('Bonus payout requested'));
    } catch (err) {
      toast.error(t('Could not request the bonus payout'), apiMessage(err, t('Please try again.')));
    }
  };

  const minimum = data?.min_withdrawal_amount;
  const interval = data?.withdrawal_interval_days;

  const facts = [
    {
      key: 'next',
      label: t('Next withdrawal date'),
      icon: 'affiliate-calendar',
      value: data?.next_withdrawal_date ? formatDate(data.next_withdrawal_date) : NA,
    },
    {
      key: 'minimum',
      label: t('Minimum withdrawal'),
      icon: 'topbar-wallet',
      value: minimum != null ? usd(minimum) : NA,
      note: minimum != null && interval ? t('Every {{count}} days', { count: interval }) : null,
    },
  ];

  return (
    <div className="aff-overview">
      <div className="aff-overview__toolbar">
        <DateSelector value={range} onChange={setRange} label={hasRange ? undefined : t('All time')} />
      </div>

      <div className="aff-overview__facts">
        {facts.map((f) => (
          <div className="aff-fact" key={f.key}>
            <span className="aff-fact__label">
              <span className="icon aff-fact__icon" style={icon(f.icon)} />
              {f.label}
            </span>
            <span className="aff-fact__value">{isLoading ? <Skeleton w="110px" h={22} /> : f.value}</span>
            {f.note && <span className="aff-fact__note">{f.note}</span>}
          </div>
        ))}

        {/* The bonus claim stands on its own — it no longer hangs off a tier. */}
        {data?.bonus && (
          <div className="aff-fact aff-fact--bonus">
            <span className="aff-fact__label">
              <span className="icon aff-fact__icon" style={icon('affiliate-check-circle')} />
              {t('Bonus available')}
            </span>
            <span className="aff-fact__value">{data.bonus_amount != null ? usd(data.bonus_amount) : NA}</span>
            <Button
              variant="light"
              className="aff-fact__action"
              height={34}
              paddingInline={16}
              loading={claiming}
              disabled={claiming}
              onClick={claimBonus}
            >
              {t('Claim Bonus')}
            </Button>
          </div>
        )}
      </div>

      {isError && <p className="aff-overview__error">{t('Could not load your affiliate overview.')}</p>}

      <SuperAffiliate superAffiliate={data?.super_affiliate} isSuperAffiliate={data?.is_super_affiliate} />
    </div>
  );
}
